import React, { useState, useEffect, useCallback } from 'react';
import { Layout, PageHeader } from '../components/Layout';
import { roomsApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Users, MapPin, Euro, Settings2 } from 'lucide-react';

export default function RoomsPage() {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    capacity: '',
    capacity_seated_table: '',
    capacity_standing: '',
    location: '',
    description: '',
    photo_url: '',
    equipment: [],
    pricing: {
      hourly_rate: 0,
      daily_rate: 0,
      package_rate: 0,
      is_free: false
    },
    pricing_by_type: {
      diocese: { half_day_rate: 0 },
      paroisse: { half_day_rate: 0 },
      eglise: { half_day_rate: 0 },
      association_ig: { half_day_rate: 0 },
      entreprise: { half_day_rate: 0 }
    },
    requires_haccp: false,
    is_supplement: false
  });
  const [newEquipment, setNewEquipment] = useState({ name: '', quantity: 1 });

  useEffect(() => {
    loadRooms();
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const loadRooms = useCallback(async () => {
    try {
      const res = await roomsApi.list();
      setRooms(res.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des salles');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleOpenDialog = (room = null) => {
    const defaultPricingByType = {
      diocese: { half_day_rate: 0 },
      paroisse: { half_day_rate: 0 },
      eglise: { half_day_rate: 0 },
      association_ig: { half_day_rate: 0 },
      entreprise: { half_day_rate: 0 }
    };
    
    if (room) {
      setSelectedRoom(room);
      let pricingByType = room.pricing_by_type || defaultPricingByType;
      // Ensure all 5 types exist with half_day_rate
      for (const t of ['diocese', 'paroisse', 'eglise', 'association_ig', 'entreprise']) {
        if (!pricingByType[t]) pricingByType[t] = { half_day_rate: 0 };
        if (pricingByType[t].half_day_rate === undefined) {
          pricingByType[t] = { half_day_rate: pricingByType[t].hourly_rate || pricingByType[t].daily_rate || 0 };
        }
      }
      
      setFormData({
        name: room.name,
        capacity: room.capacity.toString(),
        capacity_seated_table: room.capacity_seated_table ? room.capacity_seated_table.toString() : '',
        capacity_standing: room.capacity_standing ? room.capacity_standing.toString() : '',
        location: room.location,
        description: room.description,
        photo_url: room.photo_url || '',
        equipment: room.equipment || [],
        pricing: room.pricing || { hourly_rate: 0, daily_rate: 0, package_rate: 0, is_free: false },
        pricing_by_type: pricingByType,
        requires_haccp: room.requires_haccp || false,
        is_supplement: room.is_supplement || false
      });
    } else {
      setSelectedRoom(null);
      setFormData({
        name: '',
        capacity: '',
        capacity_seated_table: '',
        capacity_standing: '',
        location: '',
        description: '',
        photo_url: '',
        equipment: [],
        pricing: { hourly_rate: 0, daily_rate: 0, package_rate: 0, is_free: false },
        pricing_by_type: defaultPricingByType
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        capacity: parseInt(formData.capacity),
        capacity_seated_table: formData.capacity_seated_table ? parseInt(formData.capacity_seated_table) : null,
        capacity_standing: formData.capacity_standing ? parseInt(formData.capacity_standing) : null,
        pricing: {
          ...formData.pricing,
          hourly_rate: parseFloat(formData.pricing.hourly_rate) || 0,
          daily_rate: parseFloat(formData.pricing.daily_rate) || 0,
          package_rate: parseFloat(formData.pricing.package_rate) || 0,
        },
        pricing_by_type: formData.pricing_by_type ? {
          diocese: {
            half_day_rate: parseFloat(formData.pricing_by_type.diocese?.half_day_rate) || 0,
          },
          paroisse: {
            half_day_rate: parseFloat(formData.pricing_by_type.paroisse?.half_day_rate) || 0,
          },
          eglise: {
            half_day_rate: parseFloat(formData.pricing_by_type.eglise?.half_day_rate) || 0,
          },
          association_ig: {
            half_day_rate: parseFloat(formData.pricing_by_type.association_ig?.half_day_rate) || 0,
          },
          entreprise: {
            half_day_rate: parseFloat(formData.pricing_by_type.entreprise?.half_day_rate) || 0,
          },
        } : null
      };

      if (selectedRoom) {
        await roomsApi.update(selectedRoom.id, data);
        toast.success('Salle mise à jour');
      } else {
        await roomsApi.create(data);
        toast.success('Salle créée');
      }
      setDialogOpen(false);
      loadRooms();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async () => {
    try {
      await roomsApi.delete(selectedRoom.id);
      toast.success('Salle supprimée');
      setDeleteDialogOpen(false);
      loadRooms();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const addEquipment = () => {
    if (newEquipment.name.trim()) {
      setFormData({
        ...formData,
        equipment: [...formData.equipment, { ...newEquipment }]
      });
      setNewEquipment({ name: '', quantity: 1 });
    }
  };

  const removeEquipment = (index) => {
    setFormData({
      ...formData,
      equipment: formData.equipment.filter((_, i) => i !== index)
    });
  };

  return (
    <Layout>
      <PageHeader 
        title="Gestion des Salles" 
        description="Créez et gérez les salles disponibles à la réservation"
        actions={
          <Button 
            onClick={() => handleOpenDialog()}
            className="bg-[#1e3a8a] hover:bg-[#1e40af] btn-animate"
            data-testid="add-room-button"
          >
            <Plus className="mr-2 h-4 w-4" />
            Nouvelle salle
          </Button>
        }
      />

      <div className="p-8" data-testid="rooms-content">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="spinner" style={{ width: 40, height: 40 }} />
          </div>
        ) : rooms.length === 0 ? (
          <Card className="border-slate-200">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Settings2 className="h-12 w-12 text-slate-300 mb-4" />
              <p className="text-slate-500 text-lg">Aucune salle configurée</p>
              <p className="text-slate-400 mb-4">Commencez par créer votre première salle</p>
              <Button onClick={() => handleOpenDialog()} className="bg-[#1e3a8a] hover:bg-[#1e40af]">
                <Plus className="mr-2 h-4 w-4" />
                Créer une salle
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {rooms.map((room) => (
              <Card 
                key={room.id} 
                className="border-slate-200 card-hover overflow-hidden"
                data-testid={`room-card-${room.id}`}
              >
                {room.photo_url && (
                  <div className="h-40 bg-slate-100 overflow-hidden">
                    <img 
                      src={room.photo_url} 
                      alt={room.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-start">
                    <CardTitle className="text-lg text-[#1e3a8a]" style={{ fontFamily: 'Merriweather, serif' }}>
                      {room.name}
                    </CardTitle>
                    {room.pricing?.is_free && (
                      <Badge className="bg-emerald-100 text-emerald-800">Gratuit</Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <Users size={16} className="text-[#c5a059]" />
                      <span>Assis: {room.capacity} pers.</span>
                      {room.capacity_seated_table && (
                        <span className="text-slate-400">• A table: {room.capacity_seated_table}</span>
                      )}
                      {room.capacity_standing && (
                        <span className="text-slate-400">• Debout: {room.capacity_standing}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <MapPin size={16} className="text-[#c5a059]" />
                      <span>{room.location}</span>
                    </div>
                    {!room.pricing?.is_free && room.pricing_by_type && (
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <Euro size={16} className="text-[#c5a059]" />
                        <span>
                          {Object.entries(room.pricing_by_type)
                            .filter(([, v]) => v?.half_day_rate > 0)
                            .slice(0, 2)
                            .map(([, v]) => `${v.half_day_rate}€`)
                            .join(' - ') || 'Gratuit'
                          } /demi-journée
                        </span>
                      </div>
                    )}
                    <div className="flex gap-1 mt-1">
                      {room.requires_haccp && (
                        <Badge className="bg-red-100 text-red-800 text-xs">HACCP requis</Badge>
                      )}
                      {room.is_supplement && (
                        <Badge className="bg-purple-100 text-purple-800 text-xs">Supplément</Badge>
                      )}
                    </div>
                  </div>

                  {room.equipment && room.equipment.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {room.equipment.slice(0, 3).map((eq, i) => (
                        <Badge key={`${room.id}-eq-${eq.name}`} variant="secondary" className="text-xs">
                          {eq.name} ({eq.quantity})
                        </Badge>
                      ))}
                      {room.equipment.length > 3 && (
                        <Badge variant="secondary" className="text-xs">
                          +{room.equipment.length - 3}
                        </Badge>
                      )}
                    </div>
                  )}

                  <div className="flex gap-2 pt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleOpenDialog(room)}
                      className="flex-1 border-[#1e3a8a] text-[#1e3a8a] hover:bg-[#1e3a8a] hover:text-white"
                      data-testid={`edit-room-${room.id}`}
                    >
                      <Pencil className="mr-1 h-3 w-3" />
                      Modifier
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedRoom(room);
                        setDeleteDialogOpen(true);
                      }}
                      className="border-red-300 text-red-600 hover:bg-red-50"
                      data-testid={`delete-room-${room.id}`}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#1e3a8a]" style={{ fontFamily: 'Merriweather, serif' }}>
              {selectedRoom ? 'Modifier la salle' : 'Nouvelle salle'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nom de la salle *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="Salle Saint-Antoine"
                  data-testid="room-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Capacité assis *</Label>
                <Input
                  type="number"
                  value={formData.capacity}
                  onChange={(e) => setFormData({ ...formData, capacity: e.target.value })}
                  required
                  min="1"
                  placeholder="50"
                  data-testid="room-capacity-input"
                />
              </div>
            </div>

            {/* Additional capacities */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Capacité assis à table</Label>
                <Input
                  type="number"
                  value={formData.capacity_seated_table}
                  onChange={(e) => setFormData({ ...formData, capacity_seated_table: e.target.value })}
                  min="0"
                  placeholder="30"
                  data-testid="room-capacity-seated-table-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Capacité debout</Label>
                <Input
                  type="number"
                  value={formData.capacity_standing}
                  onChange={(e) => setFormData({ ...formData, capacity_standing: e.target.value })}
                  min="0"
                  placeholder="80"
                  data-testid="room-capacity-standing-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Localisation *</Label>
              <Input
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                required
                placeholder="Maison diocésaine, 1er étage"
                data-testid="room-location-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Grande salle lumineuse avec vue sur le jardin..."
                rows={3}
                data-testid="room-description-input"
              />
            </div>

            <div className="space-y-2">
              <Label>URL de la photo</Label>
              <Input
                value={formData.photo_url}
                onChange={(e) => setFormData({ ...formData, photo_url: e.target.value })}
                placeholder="https://..."
                data-testid="room-photo-input"
              />
            </div>

            {/* Pricing by Beneficiary Type - Complete Grid */}
            <div className="space-y-4 border-t pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-base font-semibold text-[#1e3a8a]">Grille tarifaire par type de bénéficiaire</Label>
                  <p className="text-sm text-slate-500 mt-1">Définissez les participations selon le type de bénéficiaire</p>
                </div>
                <div className="flex items-center gap-2">
                  <Label htmlFor="is_free" className="text-sm">Tout gratuit</Label>
                  <Switch
                    id="is_free"
                    checked={formData.pricing.is_free}
                    onCheckedChange={(checked) => setFormData({
                      ...formData,
                      pricing: { ...formData.pricing, is_free: checked }
                    })}
                    data-testid="room-free-switch"
                  />
                </div>
              </div>

              {!formData.pricing.is_free && (
                <div className="overflow-hidden rounded-lg border border-slate-200">
                  <table className="w-full">
                    <thead className="bg-[#1e3a8a] text-white">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Type de bénéficiaire</th>
                        <th className="px-4 py-3 text-center text-sm font-semibold">Tarif demi-journée (€)</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {[
                        { key: 'diocese', label: 'Diocèse', color: 'bg-indigo-50' },
                        { key: 'paroisse', label: 'Paroisse', color: 'bg-blue-50' },
                        { key: 'eglise', label: 'Église (mouvements, asso.)', color: 'bg-emerald-50' },
                        { key: 'association_ig', label: "Asso. d'intérêt général", color: 'bg-amber-50' },
                        { key: 'entreprise', label: 'Entreprise / Fédé. Pro', color: 'bg-orange-50' },
                      ].map((type) => (
                        <tr key={type.key} className={type.color}>
                          <td className="px-4 py-2">
                            <span className="font-medium text-slate-700">{type.label}</span>
                          </td>
                          <td className="px-4 py-2">
                            <Input
                              type="number"
                              value={formData.pricing_by_type?.[type.key]?.half_day_rate || 0}
                              onChange={(e) => setFormData({
                                ...formData,
                                pricing_by_type: {
                                  ...formData.pricing_by_type,
                                  [type.key]: {
                                    half_day_rate: parseFloat(e.target.value) || 0
                                  }
                                }
                              })}
                              min="0"
                              step="0.01"
                              placeholder="0"
                              className="text-center"
                              data-testid={`pricing-${type.key}-halfday`}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <p className="text-xs text-slate-500 px-4 py-2 bg-slate-50">
                    Règle : 1ère demi-journée = 100% | 2ème = -25% | 3ème+ = -50%
                  </p>
                  <div className="bg-slate-50 px-4 py-2 text-xs text-slate-500 italic">
                    💡 Mettez 0 pour indiquer que la mise à disposition est gratuite pour ce type de bénéficiaire
                  </div>
                </div>
              )}
            </div>

            {/* Equipment */}
            <div className="space-y-4">
              <Label className="text-base font-semibold">Équipements</Label>
              <div className="flex gap-2">
                <Input
                  value={newEquipment.name}
                  onChange={(e) => setNewEquipment({ ...newEquipment, name: e.target.value })}
                  placeholder="Ex: Vidéoprojecteur"
                  className="flex-1"
                  data-testid="equipment-name-input"
                />
                <Input
                  type="number"
                  value={newEquipment.quantity}
                  onChange={(e) => setNewEquipment({ ...newEquipment, quantity: parseInt(e.target.value) || 1 })}
                  min="1"
                  className="w-20"
                  data-testid="equipment-quantity-input"
                />
                <Button type="button" onClick={addEquipment} variant="outline" data-testid="add-equipment-button">
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.equipment.map((eq, i) => (
                  <Badge 
                    key={`form-eq-${eq.name}-${i}`} 
                    variant="secondary"
                    className="flex items-center gap-1 pr-1"
                  >
                    {eq.name} ({eq.quantity})
                    <button
                      type="button"
                      onClick={() => removeEquipment(i)}
                      className="ml-1 hover:text-red-600"
                    >
                      ×
                    </button>
                  </Badge>
                ))}
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Annuler
              </Button>
              <Button type="submit" className="bg-[#1e3a8a] hover:bg-[#1e40af]" data-testid="save-room-button">
                {selectedRoom ? 'Mettre à jour' : 'Créer'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cette salle ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. La salle "{selectedRoom?.name}" sera définitivement supprimée.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-room"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
